import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'

import { ProfileUserInitials } from '@apps-common/components/common/user'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { imageUrlReplace } from '@apps-common/helpers/url-replace'
import { ICombinedUserDetails } from '@apps-common/hooks/useCombinedUserDetails'

interface IProps {
  classes?: string
  user?: ICombinedUserDetails
  size?: 'normal' | 'large'
}

export const ProfileImage: React.FC<IProps> = ({ classes, user, size = 'normal' }) => {
  const { firstName, fullName, displayName, profileImage } = user || {}
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const name = useMemo(() => fullName || displayName || firstName, [firstName, fullName, displayName])
  const resolvedProfileImage = imageUrlReplace(profileImage || undefined)

  useEffect(() => {
    setLoading(true)
    setError(false)
  }, [resolvedProfileImage])

  if (resolvedProfileImage && !error) {
    return (
      <AnimatePresence mode='popLayout'>
        {loading && (
          <motion.div
            key='profile-image-skeleton'
            className={classNames(
              'ml-[0px] rounded-full overflow-hidden bg-surface/10 animate-pulse',
              size === 'normal' && 'w-[32px] h-[32px]',
              size === 'large' && 'w-[85px] h-[85px]'
            )}
            {...motionFadeIn(0.2)}
          />
        )}
        <motion.img
          key={resolvedProfileImage}
          src={resolvedProfileImage}
          className={classNames(
            classes,
            'ml-[0px] rounded-full overflow-hidden bg-surface/10',
            loading && 'hidden',
            size === 'normal' && 'w-[32px] h-[32px]',
            size === 'large' && 'w-[85px] h-[85px]'
          )}
          alt={`Profile for ${displayName}`}
          onLoad={() => setLoading(false)}
          onError={() => setError(true)}
        />
      </AnimatePresence>
    )
  }

  return <ProfileUserInitials classes={classes} name={name || 'N/A'} />
}
